import React, { useState, useEffect, useCallback, useRef } from 'react';
import { T, API_BASE } from '../constants';

// WURA Monitor — "Deep-Europe" NQ 3-min confluence book (european_team). Polls
// /strategies/api/strategies/wura/decisions/ every 20s. Cobra-15M entry + same-dir
// MW/Lux confluence within 15min; window deep_europe 04:30-08:00 (locked) or
// open_drive 09:00-09:30 (vol-gated, SHADOW). Trail: arm+20/give12/cap120 with a
// per-class hard stop (deep -55 / open -85). Cert WURA-DEEPEU-LOCK-…D7CCDDFF · Phase 0.

const REFRESH_MS = 20000;
const WURA_COLOR = '#ca8a04';   // gold ("wura")

const STATUS_COLORS = {
  OPEN: '#3b82f6', TRAIL: T.bull, CAP: T.gold, STOP: T.bear, SKIP: T.textMuted,
};
const classColor = (c) => (c === 'deep_europe' ? '#0891b2' : c === 'open_drive' ? '#a855f7' : T.textMuted);

function fmtET(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    const time = d.toLocaleTimeString('en-GB', { timeZone: 'America/New_York', hour12: false,
      hour: '2-digit', minute: '2-digit', second: '2-digit' });
    return `${date} ${time}`;
  } catch { return iso; }
}
const todayETStr = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
const num = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));

function Pill({ value, color, mono = false }) {
  if (value == null || value === '') return <span style={{ color: T.textMuted }}>—</span>;
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10,
      fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
      background: `${color || T.border}22`, border: `1px solid ${color || T.border}66`,
      color: color || T.text, fontFamily: mono ? 'Roboto Mono, monospace' : 'inherit' }}>{String(value)}</span>
  );
}
function Stat({ label, value, color }) {
  return (
    <div style={{ flex: '1 1 120px', padding: '12px 14px', borderRadius: 6,
      background: 'rgba(255,255,255,0.02)', border: `1px solid ${T.border}` }}>
      <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 700, letterSpacing: '0.08em',
        textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 22, color: color || T.text, fontFamily: 'Roboto Mono, monospace', marginTop: 4 }}>{value}</div>
    </div>
  );
}

export default function WuraMonitor() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [actionFilter, setActionFilter] = useState('');
  const [dirFilter, setDirFilter] = useState('');
  const [limit, setLimit] = useState(100);
  const [since, setSince] = useState('');
  const timerRef = useRef(null);

  const fetchRows = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      let url = `${API_BASE}/strategies/api/strategies/wura/decisions/?limit=${limit}`;
      if (actionFilter) url += `&action=${actionFilter}`;
      if (dirFilter) url += `&dir=${dirFilter}`;
      if (since) url += `&since=${since}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setRows(j.decisions || []);
      setLastFetched(new Date());
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  }, [actionFilter, dirFilter, limit, since]);

  useEffect(() => {
    fetchRows();
    timerRef.current = setInterval(fetchRows, REFRESH_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchRows]);

  const c = rows.reduce((a, r) => {
    if (r.action === 'TAKE') { a.takes += 1; if (!r.exit_time) a.open += 1; }
    else if (r.action === 'SHADOW') a.shadow += 1;
    else a.skips += 1;
    if (r.exit_reason) a.exits += 1;
    if (r.pnl_bp != null && r.action === 'TAKE') a.net += r.pnl_bp;
    return a;
  }, { takes: 0, skips: 0, shadow: 0, open: 0, exits: 0, net: 0 });

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', background: T.bg }}>
    <div style={{ padding: 'clamp(12px, 3vw, 20px) clamp(10px, 4vw, 28px)', maxWidth: 1800, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap',
        paddingBottom: 14, borderBottom: `1px solid ${T.border}`, marginBottom: 18 }}>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.text }}>WURA Monitor</h1>
        <span style={{ fontSize: 11, color: T.textMuted }}>
          Pre-market NQ 3m · MW+Lux+AA confluence · AA-Lull 04:30-06:30 / Deep-Europe 06:30-08:00 ·
          −50bp stop + 09:30 exit · OṢUPA-SKIP coupled · cert 92858500 · Phase 1 LIVE
          <br />Deployed 2026-06-20 22:23 WAT · Last updated 2026-06-24 18:30 WAT (Pine emitter v3.1)
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center',
          flexWrap: 'wrap', justifyContent: 'flex-end', flex: '1 1 360px' }}>
          <label style={{ fontSize: 11, color: T.textMuted }}>Since</label>
          <input type="date" value={since} max={todayETStr()} onChange={e => setSince(e.target.value)}
            style={{ padding: '4px 8px', fontSize: 12, borderRadius: 4, border: `1px solid ${T.border}`,
              background: T.bg, color: T.text, fontFamily: 'Roboto Mono, monospace' }} />
          <button onClick={() => setSince('')} style={{ padding: '4px 8px', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.05em', textTransform: 'uppercase', borderRadius: 4, cursor: 'pointer',
            border: `1px solid ${T.border}`, background: 'transparent', color: T.textMuted }}>Clear</button>
          <select value={actionFilter} onChange={e => setActionFilter(e.target.value)}
            style={{ padding: '4px 8px', fontSize: 11, borderRadius: 4, border: `1px solid ${T.border}`, background: T.bg, color: T.text }}>
            <option value="">All actions</option><option value="TAKE">TAKE</option>
            <option value="SHADOW">SHADOW</option><option value="SKIP">SKIP</option>
          </select>
          <select value={dirFilter} onChange={e => setDirFilter(e.target.value)}
            style={{ padding: '4px 8px', fontSize: 11, borderRadius: 4, border: `1px solid ${T.border}`, background: T.bg, color: T.text }}>
            <option value="">Both dirs</option><option value="LONG">LONG</option><option value="SHORT">SHORT</option>
          </select>
          <select value={limit} onChange={e => setLimit(parseInt(e.target.value, 10))}
            style={{ padding: '4px 8px', fontSize: 11, borderRadius: 4, border: `1px solid ${T.border}`, background: T.bg, color: T.text }}>
            <option value={50}>50 rows</option><option value={100}>100 rows</option>
            <option value={200}>200 rows</option><option value={500}>500 rows</option>
          </select>
          <button onClick={fetchRows} disabled={loading} style={{ padding: '5px 12px', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.07em', textTransform: 'uppercase', borderRadius: 4, cursor: loading ? 'wait' : 'pointer',
            border: 'none', background: `${WURA_COLOR}2e`, color: WURA_COLOR }}>{loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>

      {error && (
        <div style={{ padding: 10, marginBottom: 12, borderRadius: 4, background: 'rgba(239,68,68,0.1)',
          border: `1px solid ${T.bear}`, color: T.bear, fontSize: 12 }}>Error: {error}</div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <Stat label="Signals" value={rows.length} color={T.text} />
        <Stat label="TAKE" value={c.takes} color={WURA_COLOR} />
        <Stat label="Open slot" value={c.open} color={STATUS_COLORS.OPEN} />
        <Stat label="Shadow" value={c.shadow} color="#a855f7" />
        <Stat label="Exits" value={c.exits} color={T.bull} />
        <Stat label="SKIP" value={c.skips} color={T.textMuted} />
        <Stat label="Net bp (TAKE)" value={num(c.net, 1)} color={c.net >= 0 ? T.bull : T.bear} />
      </div>

      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 8, fontFamily: 'Roboto Mono, monospace' }}>
        Shadow log · {rows.length} rows · newest first
        {lastFetched && ` · updated ${lastFetched.toLocaleTimeString('en-GB', { hour12: false })} ET`}
        <span style={{ marginLeft: 10 }}>· exits: <span style={{ color: T.bull }}>time_0930</span> ·{' '}
          <span style={{ color: T.bear }}>stop</span></span>
      </div>

      <div style={{ overflowX: 'auto', border: `1px solid ${T.border}`, borderRadius: 6, background: 'rgba(255,255,255,0.01)' }}>
        <table style={{ width: '100%', minWidth: 1500, borderCollapse: 'collapse', fontSize: 11, fontFamily: 'Roboto Mono, monospace' }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.04)', textAlign: 'left', color: T.textMuted,
              fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              {['Time (ET)', 'Dir', 'Class', 'Conf', 'Status', 'Entry $', 'Stop $', 'Exit (ET)', 'Exit $',
                'PnL bp', 'Hold m', 'MFE', 'MAE', 'VIX / med', 'Reason'].map((h, i) => (
                <th key={i} style={{ padding: '8px 10px', textAlign: i >= 5 && i <= 12 ? 'right' : 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={15} style={{ padding: '20px 14px', color: T.textMuted, textAlign: 'center', fontStyle: 'italic' }}>
                No WURA decisions yet. Wire the WURA emitter (cobra_entry + mw/lux confluence + vix) to the
                webhook; every Cobra entry logs here (TAKE/SKIP/SHADOW). Auto-refresh {REFRESH_MS / 1000}s.
              </td></tr>
            )}
            {rows.map(r => {
              const st = (r.status || (r.action === 'TAKE' ? 'OPEN' : r.action)).replace('SHADOW_', '');
              const stColor = r.action === 'SHADOW' ? '#a855f7' : (STATUS_COLORS[st] || T.textMuted);
              const open = r.action === 'TAKE' && !r.exit_time;
              return (
                <tr key={r.id} style={{ borderTop: `1px solid ${T.border}`,
                  background: open ? 'rgba(59,130,246,0.06)' : 'transparent' }}>
                  <td style={{ padding: '6px 10px', color: T.text }}>{fmtET(r.bar_time)}</td>
                  <td style={{ padding: '6px 10px' }}><Pill value={r.dir} color={r.dir === 'LONG' ? T.bull : T.bear} /></td>
                  <td style={{ padding: '6px 10px' }}><Pill value={r.class === 'deep_europe' ? 'DEEP-EU' : r.class === 'open_drive' ? 'OPEN-DR' : r.class} color={classColor(r.class)} /></td>
                  <td style={{ padding: '6px 10px' }}><Pill value={r.confluence_src} color={WURA_COLOR} /></td>
                  <td style={{ padding: '6px 10px' }}><Pill value={r.action === 'SHADOW' ? `SH·${st}` : st} color={stColor} mono /></td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: T.text }}>{num(r.entry_price)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: T.bear }}>{r.stop_bp != null ? `−${num(r.stop_bp, 0)}bp` : '—'}</td>
                  <td style={{ padding: '6px 10px', color: T.textMuted }}>{r.exit_time ? fmtET(r.exit_time) : '—'}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: T.text }}>{num(r.exit_price)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700,
                    color: r.pnl_bp == null ? T.textMuted : r.pnl_bp >= 0 ? T.bull : T.bear }}>
                    {r.pnl_bp != null ? num(r.pnl_bp, 1) : '—'}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: T.textMuted }}>{r.hold_min != null ? r.hold_min : '—'}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: T.bull }}>{num(r.mfe_bp, 1)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: T.bear }}>{num(r.mae_bp, 1)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: r.vol_gate_pass === false ? T.bear : T.text }}>
                    {r.vix_at_entry != null ? `${num(r.vix_at_entry, 1)}/${num(r.vix_med_252, 1)}` : '—'}</td>
                  <td style={{ padding: '6px 10px', color: T.textMuted, fontSize: 10, maxWidth: 200,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.reason || ''}>{r.reason || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
    </div>
  );
}
